import { createTheme } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';
import { getPalette } from './palette';
import { typography } from './typography';

export const getTheme = (mode: PaletteMode) => {
  return createTheme({
    palette: getPalette(mode),
    typography,
    shape: {
      borderRadius: 8,
    },
    shadows: [
      'none',
      '0px 2px 4px rgba(0,0,0,0.05)',
      '0px 4px 8px rgba(0,0,0,0.08)',
      '0px 8px 16px rgba(0,0,0,0.1)',
      '0px 12px 24px rgba(0,0,0,0.12)',
      '0px 16px 32px rgba(0,0,0,0.14)',
      '0px 20px 40px rgba(0,0,0,0.16)',
      '0px 24px 48px rgba(0,0,0,0.18)',
      '0px 2px 4px rgba(0,0,0,0.05)',
      '0px 4px 8px rgba(0,0,0,0.08)',
      '0px 8px 16px rgba(0,0,0,0.1)',
      '0px 12px 24px rgba(0,0,0,0.12)',
      '0px 16px 32px rgba(0,0,0,0.14)',
      '0px 20px 40px rgba(0,0,0,0.16)',
      '0px 24px 48px rgba(0,0,0,0.18)',
      '0px 2px 4px rgba(0,0,0,0.05)',
      '0px 4px 8px rgba(0,0,0,0.08)',
      '0px 8px 16px rgba(0,0,0,0.1)',
      '0px 12px 24px rgba(0,0,0,0.12)',
      '0px 16px 32px rgba(0,0,0,0.14)',
      '0px 20px 40px rgba(0,0,0,0.16)',
      '0px 24px 48px rgba(0,0,0,0.18)',
      '0px 2px 4px rgba(0,0,0,0.05)',
      '0px 4px 8px rgba(0,0,0,0.08)',
      '0px 8px 16px rgba(0,0,0,0.1)',
    ],
    components: {
      // Gráficos (Recharts) no modo escuro: o tooltip vinha branco com o texto a herdar
      // o branco do body (texto invisível), e a grelha/eixos com cinzentos claros fixos.
      // As cores dos gráficos estão escritas nos ecrãs; aqui sobrepõem-se por CSS.
      ...(mode === 'dark' && {
        MuiCssBaseline: {
          styleOverrides: {
            '.recharts-default-tooltip': {
              backgroundColor: '#25253E !important',
              borderColor: '#3D3D5C !important',
              color: '#FFFFFF',
            },
            '.recharts-tooltip-label': { color: '#FFFFFF' },
            '.recharts-cartesian-grid line': { stroke: 'rgba(255, 255, 255, 0.08)' },
            '.recharts-cartesian-axis-line, .recharts-cartesian-axis-tick-line': { stroke: '#3D3D5C' },
            '.recharts-cartesian-axis-tick-value': { fill: '#B0B0C0' },
            '.recharts-legend-item-text': { color: '#B0B0C0 !important' },
          },
        },
      }),
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 8,
            padding: '8px 16px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0px 4px 8px rgba(0,0,0,0.12)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          elevation1: {
            boxShadow: '0px 2px 4px rgba(0,0,0,0.05)',
          },
          elevation2: {
            boxShadow: '0px 4px 8px rgba(0,0,0,0.08)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: 'none',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            paddingTop: '16px !important',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: mode === 'light' ? '#6B6B80' : '#b0b0b0',
            '&.Mui-focused': {
              fontWeight: 500,
              color: mode === 'light' ? '#6233A0' : '#9060CB',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            minHeight: '1.4375em',
            color: mode === 'light' ? '#212121' : '#ffffff',
          },
          icon: {
            color: mode === 'light' ? '#6B6B80' : '#b0b0b0',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: mode === 'light' ? '#212121' : '#ffffff',
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.09)',
            '&:hover': {
              backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.09)' : 'rgba(255, 255, 255, 0.13)',
            },
            '&.Mui-focused': {
              backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.09)',
            },
          },
          input: {
            color: mode === 'light' ? '#212121' : '#ffffff',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            color: mode === 'light' ? '#212121' : '#ffffff',
          },
          input: {
            color: mode === 'light' ? '#212121' : '#ffffff',
            '&::placeholder': {
              color: mode === 'light' ? '#757575' : '#b0b0b0',
              opacity: 1,
            },
          },
        },
      },
    },
  });
};
